import React, {useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {Button, Typography} from "@mui/material";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, {linearProgressClasses} from '@mui/material/LinearProgress';
import {styled} from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Divider from '@mui/material/Divider';
import historyStore, {selectedDevicesOf} from "../store/historyStore";
import {MAX_DEVICE_COUNT, getDeviceListByIds} from "../utils/deviceInfo";
import {registeredDeviceIds, useHealthDevice} from "../utils/healthDeviceApi";

const HistoryDeviceCard = (props: any) => {
  const navigate = useNavigate();
  const storeData: any = historyStore();
  // 이전 계정의 선택이 넘어오지 않도록 소유자를 확인하고 읽는다
  const selectedDeviceList: string[] = selectedDevicesOf(storeData);
  const selectedDevices = getDeviceListByIds(selectedDeviceList);

  /**
   * 서버에 등록된 기기로 로컬 선택을 맞춘다.
   *
   * 기기 선택은 AP_009_003 으로 계정별 저장되므로 서버가 정본이다. 브라우저를 바꾸거나
   * 계정을 바꾸면 로컬은 비어 있는데 서버에는 등록이 남아 있어, 이 카드가 '기기를 선택해
   * 주세요' 로만 보였다. 여기서 한 번 맞춰 두면 기록 홈의 측정값 카드도 함께 살아난다.
   *
   * 조회는 측정값 카드와 같은 훅·같은 날짜라 모듈 캐시가 합쳐 준다(요청이 늘지 않는다).
   * 서버에 등록이 하나도 없으면 건드리지 않는다 — 서버 저장에 실패해 이 기기에만 남아
   * 있는 선택을 지우지 않기 위해서다.
   */
  const { data: deviceData } = useHealthDevice(0);
  const serverKey = registeredDeviceIds(deviceData).join(',');

  useEffect(() => {
    if (serverKey === '') return;
    if (selectedDevicesOf(historyStore.getState()).join(',') === serverKey) return;
    historyStore.getState().setSelectedDeviceList(serverKey.split(','));
  }, [serverKey]);

  function getTitleForDevice() {
    if (selectedDevices.length === 0) {
      return '연동할 기기를 선택해 주세요.';
    }
    return '기기 ' + selectedDevices.length + '대를 선택하셨습니다.';
  }

  function getBorderProgressValue() {
    return Math.round(selectedDevices.length / MAX_DEVICE_COUNT * 100);
  }

  return (
    <Box alignItems="stretch" sx={Card_Style}>

      <Typography sx={{ textAlign: 'center', fontSize: 14, fontWeight: 700, p: '15px 0 15px 0' }}>기기 선택</Typography>
      <Divider />
      <Box sx={{ p: 0.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack justifyContent={'center'} alignItems={'center'} sx={{ flexGrow: 1 }}>

          <Stack justifyContent={'center'} alignItems={selectedDevices.length === 0 ? 'center' : 'flex-start'}
            sx={{ width: '100%', flexGrow: 1, pl: 2, pr: 1, overflow: 'hidden', boxSizing: 'border-box' }}>
            {selectedDevices.length === 0 ? (
              <Button onClick={() => { navigate('../history/HistoryDevice') }}
                sx={{ minWidth: 0, width: 56, height: 56, borderRadius: '50%', border: '2px dashed #EF8322' }}>
                <AddRoundedIcon sx={{ color: '#EF8322', fontSize: 30 }} />
              </Button>
            ) : (
              selectedDevices.map((device) => (
                // alignItems 는 세로 flex 에서 '가로' 를 정한다. flex-start 면 글자가
                // 제 길이만큼 늘어나 자기 폭을 넘지 않으므로 noWrap 의 말줄임이 발동하지
                // 않고 부모 overflow:hidden 에 '...' 없이 잘렸다. stretch 로 칸 폭을 채운다.
                <Stack key={device.id} direction={'column'} alignItems={'stretch'}
                  onClick={() => { navigate('../history/HistoryDeviceDetail', { state: { deviceId: device.id } }) }}
                  // minWidth 0 이 없으면 긴 기기명이 칸을 밀어 넓힌다
                  sx={{ width: '100%', minWidth: 0, py: '4px', cursor: 'pointer' }}>
                  <Typography noWrap sx={{ color: '#888', fontSize: 13, lineHeight: 1.35 }}>{device.typeName}</Typography>
                  <Typography noWrap title={device.name} sx={{ color: '#555', fontSize: 15, fontWeight: 700, lineHeight: 1.35, }}>· {device.name}</Typography>
                </Stack>
              ))
            )}
          </Stack>

          {/* 미선택 상태는 카드가 짧아 여백이 안 생기므로 위쪽 간격을 직접 넣어줌 */}
          <Box sx={{ pt: selectedDevices.length === 0 ? 3 : 0.5, pb: 2 }}>
            <Button onClick={() => { navigate('../history/HistoryDevice') }} sx={{ p: 0, border: '1px solid #EF8322', borderRadius: 5 }}>
              <AddRoundedIcon sx={{ ml: 1, color: '#EF8322' }} />
              <Typography sx={{ color: '#EF8322', fontSize: 14, pr: 1, fontWeight: 700, pl: .5 }}>기기선택</Typography>
            </Button>
          </Box>
        </Stack>
        <Divider />
        <Box sx={{ p: 0.5, }}>
          <Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 32, color: '#555' }}> {getTitleForDevice()}</Typography>
        </Box>
      </Box>

      <BorderLinearProgress variant="determinate" value={getBorderProgressValue()}
        sx={{ borderRadius: '0 0 20px 20px', bgcolor: '#eaeaea' }}
      />
    </Box>
  );
}

const BorderLinearProgress = styled(LinearProgress)({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#eaeaea',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#EF8322',
  },
});

const Card_Style = {
  borderRadius: '10px',
  background: '#fff',
  boxShadow: '0 0 6px  rgba(0,0,0,0.2)',

  flex: '0 50%',
  maxWidth: '100%',

  // 옆 카드와 높이를 맞추기 위해 세로 flex 로 구성 (하단 진행바는 항상 카드 맨 아래)
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column'
};

export default HistoryDeviceCard;
